import React, { useRef } from 'react';
import { Ellipsis } from 'lucide-react';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
import WikiIcon from './WikiIcon';

/**
 * The "⋯" menu on a category or page header (#872): Settings, Move, Archive.
 * Built on the app's Radix DropdownMenu (`modal={false}`, as in
 * components/calendar/EventQuickView.jsx). It lives in the page header, never
 * inside a Wiki modal (Radix sits at z-50, Wiki modals at z 91+).
 *
 * `items`: `{ id, label, icon, onSelect, danger?, testId?, action? }`; falsy
 * entries are skipped, and the menu renders nothing when no item is left, so
 * a role with no actions gets no button. `icon` is a WikiIcon name or a
 * lucide component.
 *
 * When an item opens a Wiki modal, Radix would move focus back to the trigger
 * as the menu closes, behind the modal; the modal's own autofocus wins instead.
 */
export default function WikiActionsMenu({ items, label = 'More actions', size, testId }) {
    const selected = useRef(false);
    const visible = (items || []).filter(Boolean);
    if (visible.length === 0) return null;
    return (
        <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
                <button
                    type="button"
                    className={`wk-btn wk-btn--secondary wk-actions-trigger${size === 'sm' ? ' wk-btn--sm' : ''}`}
                    aria-label={label}
                    title={label}
                    data-testid={testId}
                >
                    <WikiIcon name={Ellipsis} size={size === 'sm' ? 13 : 15} />
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
                align="end"
                className="min-w-[180px]"
                data-testid={testId ? `${testId}-menu` : undefined}
                onCloseAutoFocus={(event) => {
                    if (!selected.current) return;
                    selected.current = false;
                    event.preventDefault();
                }}
            >
                {visible.map((item) => (
                    <DropdownMenuItem
                        key={item.id}
                        className={item.danger ? 'text-red-700 focus:text-red-700' : undefined}
                        data-testid={item.testId}
                        data-action={item.action}
                        onSelect={() => {
                            selected.current = true;
                            item.onSelect();
                        }}
                    >
                        <WikiIcon name={item.icon} size={14} style={{ marginRight: 8 }} />
                        {item.label}
                    </DropdownMenuItem>
                ))}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
